import { computed, ref, watchEffect } from 'vue'

let search = window.location.search || ''
let params = new URLSearchParams(search)
const paramsRef = new ref(params)

function setQueryParams(p) {
    let updated = p.toString()

    if (updated === search) {
        return
    }

    if (updated != '') {
        updated = '?' + updated
    }

    search = updated
    params = p
    paramsRef.value = p

    const l = document.location.origin + document.location.pathname + updated + document.location.hash
    window.history.replaceState(window.history.state, window.document.title, l)
}

function onChanged() {
    let updated = window.location.search || ''

    if (updated.startsWith('?')) {
        updated = updated.substring(1)
    }

    if (updated !== search) {
        search = updated

        params = new URLSearchParams(search)
        paramsRef.value = params
    }
}

window.addEventListener('popstate', onChanged, { passive: true })

export function useQueryParams() {
    return new Proxy(paramsRef, {
        get: (target, property) => {
            const v = target.value.getAll(property)

            if (v.length === 0) {
                return null
            }

            if (v.length === 1) {
                return v[0]
            }

            return v
        },
    })
}

export function queryParam(name, options = { initial: null, isArray: false }) {
    const { initial, isArray } = options

    return computed({
        get() {
            const all = paramsRef.value.getAll(name)

            if (isArray) {
                return all
            }

            if (all.length === 0) {
                return null
            }

            if (all.length === 1) {
                return all[0]
            }

            return all
        },
        set(value) {
            {
                value ??= null

                if (value === initial) {
                    value = null
                }

                const p = new URLSearchParams(params)

                if (value === null) {
                    p.delete(name)
                } else if (value.forEach) {
                    p.delete(name)
                    value.forEach(v => p.append(name, v))
                } else {
                    p.set(name, value)
                }

                setQueryParams(p)
            }
        },
    })
}

export function syncQuery(callback, options = { keys: [], preserve: [] }) {
    const keys = structuredClone(options?.only ?? [])
    const preserve = structuredClone(options?.preserve ?? [])

    if (keys.length > 0) {
        return watchEffect(() => {
            const query = callback()

            if (query === false) {
                return
            }

            const updated = new URLSearchParams(params)
            const set = new Set(keys)

            Object.entries(query).forEach(([key, value]) => {
                set.delete(key)
                if (isIterable(value)) {
                    updated.delete(key)
                    for (const v of value) {
                        updated.append(key, v)
                    }
                } else {
                    updated.set(key, value)
                }
            })

            for (const key of set) {
                updated.delete(key)
            }

            setQueryParams(updated)
        })
    }

    return watchEffect(() => {
        const query = callback()

        if (query === false) {
            return
        }

        const updated = new URLSearchParams()

        const remain = new Set(preserve)

        Object.entries(query).forEach(([key, value]) => {
            remain.delete(key)
            if (isIterable(value)) {
                for (const v of value) {
                    updated.append(key, v)
                }
            } else {
                updated.set(key, value)
            }
        })

        remain.forEach(key => {
            params.getAll(key).forEach(value => updated.append(key, value))
        })

        setQueryParams(updated)
    })
}

function isIterable(obj) {
    if (obj == null) {
        return false
    }
    return typeof obj !== 'string' && typeof obj[Symbol.iterator] === 'function'
}
